import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { computeTableRows, computeTotalsRow, formatComputed } from '../../lib/formCalc';

/**
 * Editable calculation table with optional totals row.
 * Props:
 *   field — form field config with `calc_table_config`
 *   value — current grid stored in responses (shape: { rows: [{colKey: val}], initialized: bool })
 *   onChange — called with the updated grid object
 *   outerFields — outer form responses (for formulas that reference {field_id})
 */
export default function CalcTableRenderer({ field, value, onChange, outerFields = {} }) {
  const cfg = field.calc_table_config || { columns: [], totals: { enabled: false, cells: {} } };
  const columns = cfg.columns || [];

  // Initialize rows on first render
  const storedRows = Array.isArray(value?.rows) ? value.rows : null;
  const initialRows = cfg.initial_rows || 1;
  const defaultRows = Array.isArray(cfg.default_rows) ? cfg.default_rows : [];
  const buildSeed = () => {
    if (defaultRows.length > 0) {
      // Deep-clone pre-filled rows so user edits don't mutate the form config.
      return defaultRows.map(r => ({ ...r }));
    }
    return Array.from({ length: initialRows }, () => Object.fromEntries(columns.map(c => [c.key, ''])));
  };
  const rawRows = storedRows || buildSeed();

  // Compute formula cells
  const computedRows = computeTableRows(cfg, rawRows, { fields: outerFields });
  const totalsRow = computeTotalsRow(cfg, computedRows, { fields: outerFields });

  const updateRows = (newRows) => onChange({ ...(value || {}), rows: newRows });

  const updateCell = (rIdx, colKey, newVal) => {
    const next = rawRows.map((r, i) => i === rIdx ? { ...r, [colKey]: newVal } : r);
    updateRows(next);
  };

  const addRow = () => updateRows([...rawRows, Object.fromEntries(columns.map(c => [c.key, '']))]);
  const removeRow = (i) => updateRows(rawRows.filter((_, x) => x !== i));

  return (
    <div className="space-y-2" data-testid={`calc-table-${field.field_id}`}>
      {field.label ? <Label>{field.label}</Label> : null}
      <div className="overflow-x-auto border border-[#E2E8F0] rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#F1F5F9]">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{c.label}{c.type === 'formula' && <span className="text-[9px] text-[#94A3B8] ml-1">(auto)</span>}</th>
              ))}
              {cfg.allow_add_rows && <th className="p-2 border-b w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {computedRows.map((row, ri) => (
              <tr key={ri} className="border-b border-[#E2E8F0]" data-testid={`calc-table-row-${field.field_id}-${ri}`}>
                {columns.map((col) => (
                  <td key={col.key} className="p-1.5">
                    {col.type === 'formula' ? (
                      <div className="px-2 py-1 bg-[#EFF6FF] rounded text-[#1E40AF] font-mono text-sm" data-testid={`calc-cell-${field.field_id}-${ri}-${col.key}`}>
                        {formatComputed(row[col.key], col.format || field.format || { type: 'number', decimals: 0 })}
                      </div>
                    ) : (
                      <Input
                        type={col.type === 'number' ? 'number' : 'text'}
                        value={rawRows[ri]?.[col.key] ?? ''}
                        onChange={(e) => updateCell(ri, col.key, col.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                        className="h-8 text-sm"
                        data-testid={`calc-input-${field.field_id}-${ri}-${col.key}`}
                      />
                    )}
                  </td>
                ))}
                {cfg.allow_add_rows && (
                  <td className="p-1.5 text-center">
                    {computedRows.length > 1 && (
                      <button type="button" onClick={() => removeRow(ri)} className="text-[#991B1B] hover:text-[#7F1D1D]" aria-label="Remove row" data-testid={`calc-table-remove-row-${field.field_id}-${ri}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {totalsRow && (
              <tr className="bg-[#F8FAFC] font-semibold border-t-2 border-[#0E7490]" data-testid={`calc-table-totals-${field.field_id}`}>
                {columns.map((col) => (
                  <td key={col.key} className="p-2 text-sm">
                    {totalsRow[col.key] !== undefined && totalsRow[col.key] !== null ? (
                      <span className="text-[#0E7490]">{formatComputed(totalsRow[col.key], col.format || field.format || { type: 'number', decimals: 0 })}</span>
                    ) : (
                      col === columns[0] ? <span className="text-[#475569]">Total</span> : ''
                    )}
                  </td>
                ))}
                {cfg.allow_add_rows && <td></td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {cfg.allow_add_rows && (
        <Button type="button" variant="outline" size="sm" onClick={addRow} className="h-8 text-xs" data-testid={`calc-table-add-row-${field.field_id}`}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add Row
        </Button>
      )}
    </div>
  );
}
